import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useUploadAccountLogoMutation } from '../../../../queries';
import { AccountOperationsViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { ImageFileChooserModal } from '../../../../web/components/ImageFileChooserModal';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	styles?: StyleDeclarationValue[];
	accountOperations: AccountOperationsViewModel;
}

export const AccountLogoField = inject(ImpersonationContextKey)(
	observer(function AccountLogoField({ accountOperations, impersonationContext, styles = [] }: IProps) {
		const errorMessages = useErrorMessages();
		const [showFileInputModal, setShowFileInputModal] = React.useState(false);
		const uploadLogoMutation = useUploadAccountLogoMutation({
			onSuccess: account => {
				if (account.preferences?.logo) {
					accountOperations.setAccountLogo(account.preferences.logo);
				}
				setShowFileInputModal(false);
			},
			onError: error => {
				setShowFileInputModal(false);
				errorMessages.pushApiError(error);
			},
		});

		const onFileSelected = (logo: File) => {
			uploadLogoMutation.mutate({ logo, impersonationContext });
		};

		const logoUrl = accountOperations.account?.preferences?.logo?.url;
		const renderLogoLabel = () => {
			if (logoUrl) {
				return (
					<span>
						<span>Logo</span>
						&nbsp;
						{uploadLogoMutation.isPending ? (
							<LoadingSpinner type='extra-tiny' className={css(baseStyleSheet.inlineBlock)} />
						) : (
							<>
								(
								<button
									className={css(baseStyleSheet.brandLink, styleSheet.editButton)}
									onClick={() => setShowFileInputModal(true)}
								>
									<span>Edit</span>
								</button>
								)
							</>
						)}
					</span>
				);
			}
			return 'Logo';
		};
		return (
			<AccountDetailsInfoField label={renderLogoLabel()} styles={[...styles]} valueStyles={[styleSheet.container]}>
				<ConditionalAccess permission={AdminPermission.ModifyAccount} elseChildren={null}>
					<>
						{logoUrl ? (
							<img className={css(styleSheet.accountLogo)} src={logoUrl} />
						) : (
							<button className={css(baseStyleSheet.brandLink)} onClick={() => setShowFileInputModal(true)}>
								<span>Upload Image</span>
							</button>
						)}
						<ImageFileChooserModal
							title='Upload Logo'
							modalProps={{
								isOpen: showFileInputModal,
								onRequestClose: (result, cancel) => {
									if (!cancel && result) {
										onFileSelected(result);
									} else {
										setShowFileInputModal(false);
									}
								},
							}}
						/>
					</>
				</ConditionalAccess>
			</AccountDetailsInfoField>
		);
	})
);
